import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, deleteDoc, query, where, getDoc, updateDoc, onSnapshot, orderBy, addDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import EditHouseModal from './EditHouseModal';
import AddScenarioModal from './AddScenarioModal';
import Breadcrumb from './Breadcrumb';
import LoadingSpinner from './LoadingSpinner';
import { useFormattedNumber } from '../hooks/useFormattedNumber';
import TransactionHistory from './TransactionHistory';
import styles from './HouseDetail.module.css';
import BaseModal from './common/BaseModal';
import FileManager from './common/FileManager';
import AddCompModal from './AddCompModal';
import Dashboard from './Dashboard';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const HOUSE_STATUSES = {
  UNDERWRITING: 'Underwriting',
  UNQUALIFIED: 'Unqualified',
  UNDER_CONTRACT: 'Under Contract',
  UNDER_MANAGEMENT: 'Under Management',
  DIVESTED: 'Divested'
};

const HOUSE_STATUS_CLASSES = {
  UNDERWRITING: 'bg-primary',
  UNQUALIFIED: 'bg-secondary',
  UNDER_CONTRACT: 'bg-warning',
  UNDER_MANAGEMENT: 'bg-success',
  DIVESTED: 'bg-info'
};

const SCENARIO_STATUS_CLASSES = {
  'Best': 'bg-success',
  'Good': 'bg-info',
  'Risky': 'bg-warning',
  'Poor': 'bg-danger'
};

const HouseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [house, setHouse] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isScenarioModalOpen, setIsScenarioModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [returnTargets, setReturnTargets] = useState({
    cashOnCash: '',
    internalRate: '',
    capRate: ''
  });
  const [showStatusPopover, setShowStatusPopover] = useState(false);
  const statusPopoverRef = useRef(null);
  const statusButtonRef = useRef(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(true); 
  const [isComparing, setIsComparing] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [editingTargets, setEditingTargets] = useState(false);
  const [savingTargets, setSavingTargets] = useState(false);
  const [editingInitialInvestment, setEditingInitialInvestment] = useState(false);
  const [savingInitialInvestment, setSavingInitialInvestment] = useState(false);
  const [editingCurrentValue, setEditingCurrentValue] = useState(false);
  const [savingCurrentValue, setSavingCurrentValue] = useState(false);
  const [editingInvestmentValue, setEditingInvestmentValue] = useState(false);
  const [savingInvestmentValue, setSavingInvestmentValue] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [editedInvestment, setEditedInvestment] = useState({
    purchasePrice: '',
    downPaymentPercent: '',
    closingCosts: '',
    interestRate: ''
  });
  
  // Regular form fields
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [link, setLink] = useState('');
  const [targetCapRate, setTargetCapRate] = useState('');
  const [targetCoCReturn, setTargetCoCReturn] = useState('');
  const [targetTotalROI, setTargetTotalROI] = useState('');

  // Formatted number fields
  const listPriceFormat = useFormattedNumber('');
  const squareFootageFormat = useFormattedNumber('');

  const purchasePriceFormat = useFormattedNumber('');
  const downPaymentPercentFormat = useFormattedNumber('');
  const closingCostsFormat = useFormattedNumber('');
  const interestRateFormat = useFormattedNumber('');
  const currentValueFormat = useFormattedNumber('');
  const mortgageBalanceFormat = useFormattedNumber('');
  const salePriceFormat = useFormattedNumber('');
  const proceedsFormat = useFormattedNumber('');

  const [closeDate, setCloseDate] = useState('');

  const [transactions, setTransactions] = useState([]);

  const [returnViewMode, setReturnViewMode] = useState('pre-sale');
  const [toggleError, setToggleError] = useState('');

  const [showSaleSimulator, setShowSaleSimulator] = useState(false);
  const [saleSimulation, setSaleSimulation] = useState({
    mortgageBalance: 0,
    salePrice: 0,
    salePercentage: 94,
  });

  const [showSellModal, setShowSellModal] = useState(false);
  const [saleData, setSaleData] = useState({
    status: 'DIVESTED',
    salePrice: '',
    formattedSalePrice: '',
    proceedsPercentage: '94'
  });
  const [isSaving, setIsSaving] = useState(false);

  const [editingSaleDetails, setEditingSaleDetails] = useState(false);
  const [saleDate, setSaleDate] = useState('');

  const [isCompModalOpen, setIsCompModalOpen] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [comps, setComps] = useState([]);
  const [showDeleteCompModal, setShowDeleteCompModal] = useState(false);
  const [compToDelete, setCompToDelete] = useState(null);
  const [isDeletingComp, setIsDeletingComp] = useState(false);

  const [scenariosCurrentPage, setScenarioCurrentPage] = useState(1);
  const [scenariosPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  useEffect(() => {
    if (showSellModal && house) {
      setSaleData({
        status: 'DIVESTED',
        salePrice: house.currentValue?.toString() || '',
        formattedSalePrice: Number(house.currentValue || 0).toLocaleString(),
        proceedsPercentage: '94'
      });
    }
  }, [showSellModal, house]);

  useEffect(() => {
    if (editingSaleDetails) {
      // Only update the form values when entering edit mode
      salePriceFormat.onChange({ target: { value: house?.finalSalePrice?.toString() || '' } });
      proceedsFormat.onChange({ target: { value: house?.totalAmountReceived?.toString() || '' } });
      setSaleDate(house?.saleDate || '');
    }
  }, [editingSaleDetails, house]);

  useEffect(() => {
    let unsubscribe = null;
    
    // Only set up the listener if we have a valid house and ID
    if (id && house) {
      try {
        unsubscribe = onSnapshot(
          collection(db, 'properties', id, 'comps'),
          (snapshot) => {
            const compsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setComps(compsData);
          },
          (error) => {
            console.error('Error in comps listener:', error);
            setComps([]);
          }
        );
      } catch (error) {
        console.error('Failed to set up comps listener:', error);
      }
    } else {
      // Reset comps if no house or id
      setComps([]);
    }
    
    // Clean up listener on unmount or when dependencies change
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [id, house]);

  useEffect(() => {
    if (house) {
      // Set default view mode based on whether sale data is available
      if (house.finalSalePrice && house.totalAmountReceived && house.saleDate) {
        setReturnViewMode('post-sale');
      } else {
        setReturnViewMode('pre-sale');
      }
    }
  }, [house]);

  const getMetricColor = (actual, target) => {
    if (!actual) return 'inherit';
    return parseFloat(actual) >= parseFloat(target) ? '#4caf50' : '#f44336';
  };

  const fetchData = async (retryCount = 0) => {
    try {
      setIsLoading(true);
      const docRef = doc(db, 'properties', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setHouse(data);
        
        // Initialize target metrics
        setTargetCapRate(data.targetCapRate || '');
        setTargetCoCReturn(data.targetCoCReturn || '');
        setTargetTotalROI(data.targetTotalROI || '');

        // Fetch scenarios
        try {
          const scenariosQuery = query(
            collection(db, 'scenarios'),
            where('houseId', '==', id)
          );
          const scenariosSnapshot = await getDocs(scenariosQuery);
          const scenariosData = scenariosSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setScenarios(scenariosData);
        } catch (scenarioError) {
          console.error('Error fetching scenarios:', scenarioError);
          // Continue even if scenarios can't be fetched
          setScenarios([]);
        }

        // Initialize return targets
        setReturnTargets({
          cashOnCash: data.returnTargets?.cashOnCash || '',
          internalRate: data.returnTargets?.internalRate || '',
          capRate: data.returnTargets?.capRate || ''
        });
        
        setIsLoading(false);
      } else {
        // Document doesn't exist - if we've just created it, try again
        if (retryCount < 3) {
          console.log(`Property not found, retrying (${retryCount + 1}/3)...`);
          // Wait a bit longer each retry
          setTimeout(() => fetchData(retryCount + 1), 1000 * (retryCount + 1));
          return;
        } else {
          // After 3 retries, give up and redirect
          setHouse(null);
          setIsLoading(false);
          setTimeout(() => {
            navigate('/');
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
      
      // For permission errors, retry a few times
      if (error.code === 'permission-denied' && retryCount < 3) {
        console.log(`Permission denied, retrying (${retryCount + 1}/3)...`);
        setTimeout(() => fetchData(retryCount + 1), 1000 * (retryCount + 1));
        return;
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    fetchData();
    // Cleanup: reset state to avoid stale listeners/data
    return () => {
      isMounted = false;
      setHouse(null);
      setScenarios([]);
      setIsLoading(true);
    };
  }, [id]);

  useEffect(() => {
    if (house) {
      listPriceFormat.onChange({ target: { value: house.listPrice?.toString() || '' } });
      squareFootageFormat.onChange({ target: { value: house.squareFootage?.toString() || '' } });
      purchasePriceFormat.onChange({ target: { value: house.purchasePrice?.toString() || '' } });
      downPaymentPercentFormat.onChange({ target: { value: house.downPaymentPercent?.toString() || '' } });
      closingCostsFormat.onChange({ target: { value: house.closingCosts?.toString() || '' } });
      interestRateFormat.onChange({ target: { value: house.interestRate?.toString() || '' } });
      currentValueFormat.onChange({ target: { value: house.currentValue?.toString() || '' } });
      mortgageBalanceFormat.onChange({ target: { value: house.mortgageBalance?.toString() || '0' } });
      salePriceFormat.onChange({ target: { value: house.finalSalePrice?.toString() || '' } });
      
      console.log('Initializing formatted values:', {
        mortgageBalance: house.mortgageBalance,
        currentValue: house.currentValue
      });
    }
  }, [house]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusPopover && 
          statusPopoverRef.current && 
          statusButtonRef.current &&
          !statusPopoverRef.current.contains(event.target) &&
          !statusButtonRef.current.contains(event.target)) {
        setShowStatusPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusPopover]);

  const handleDropdownToggle = (scenarioId, isOpen) => {
    setOpenDropdownId(isOpen ? scenarioId : null);
  };

  const hasChanges = () => {
    if (!house?.returnTargets) return false;
    return returnTargets.cashOnCash !== (house.returnTargets.cashOnCash || '') ||
           returnTargets.internalRate !== (house.returnTargets.internalRate || '') ||
           returnTargets.capRate !== (house.returnTargets.capRate || '');
  };

  const handleReturnTargetChange = (field, value) => {
    setReturnTargets(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveReturnTargets = async () => {
    setIsLoading(true);
    try {
      const docRef = doc(db, 'properties', id);
      await updateDoc(docRef, {
        returnTargets: {
          cashOnCash: returnTargets.cashOnCash,
          internalRate: returnTargets.internalRate,
          capRate: returnTargets.capRate
        }
      });
    } catch (error) {
      console.error('Error updating return targets:', error);
    }
    setIsLoading(false);
  };

  const cancelChanges = () => {
    setReturnTargets({
      cashOnCash: house.returnTargets?.cashOnCash || '',
      internalRate: house.returnTargets?.internalRate || '',
      capRate: house.returnTargets?.capRate || ''
    });
  };

  const handleDeleteScenario = async () => {
    try {
      if (!scenarioToDelete) return;
      setIsDeleting(true);
      
      await deleteDoc(doc(db, 'scenarios', scenarioToDelete.id));
      setScenarios(prev => prev.filter(s => s.id !== scenarioToDelete.id));
      setShowDeleteModal(false);
      setScenarioToDelete(null);
    } catch (error) {
      console.error('Error deleting scenario:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleScenarioSelect = (scenarioId) => {
    setSelectedScenarios(prev => {
      if (prev.includes(scenarioId)) {
        return prev.filter(id => id !== scenarioId);
      }
      if (prev.length < 3) {
        return [...prev, scenarioId];
      }
      return prev;
    });
  };

  const handleEditDetails = () => {
    setEditingDetails(true);
    setEditingTargets(true);
    
    // Initialize house details form fields
    setBeds(house.beds?.toString() || '');
    setBaths(house.baths?.toString() || '');
    setLink(house.link || '');
    
    // Initialize target metrics form fields
    setTargetCapRate(house.targetCapRate?.toString() || '');
    setTargetCoCReturn(house.targetCoCReturn?.toString() || '');
    setTargetTotalROI(house.targetTotalROI?.toString() || '');
    
    // Simulate change events for formatted fields
    const listPriceEvent = { target: { value: house.listPrice?.toString() || '' } };
    const squareFootageEvent = { target: { value: house.squareFootage?.toString() || '' } };
    listPriceFormat.onChange(listPriceEvent);
    squareFootageFormat.onChange(squareFootageEvent);
  };

  const handleDetailChange = (field, value) => {
    switch (field) {
      case 'beds':
        setBeds(value);
        break;
      case 'baths':
        setBaths(value);
        break;
      default:
        break;
    }
  };

  const handleSaveCombinedDetails = async () => {
    try {
      setSavingDetails(true);
      setSavingTargets(true);
      const docRef = doc(db, 'properties', id);
      
      const updatedData = {
        listPrice: Number(listPriceFormat.value || 0),
        beds: Number(beds || 0),
        baths: Number(baths || 0),
        squareFootage: Number(squareFootageFormat.value || 0),
        link,
        targetCapRate: Number(targetCapRate || 0),
        targetCoCReturn: Number(targetCoCReturn || 0),
        targetTotalROI: Number(targetTotalROI || 0),
        updatedAt: new Date().toISOString()
      };

      await updateDoc(docRef, updatedData);
      setHouse(prev => ({
        ...prev,
        ...updatedData
      }));
      setEditingDetails(false);
      setEditingTargets(false);
    } catch (error) {
      console.error('Error updating house details:', error);
    } finally {
      setSavingDetails(false);
      setSavingTargets(false);
    }
  };

  const handleEditInvestmentValue = () => {
    setEditingInvestmentValue(true);
    
    // Initialize Initial Investment form fields
    purchasePriceFormat.onChange({ target: { value: house.purchasePrice?.toString() || '' } });
    downPaymentPercentFormat.onChange({ target: { value: house.downPaymentPercent?.toString() || '' } });
    closingCostsFormat.onChange({ target: { value: house.closingCosts?.toString() || '' } });
    interestRateFormat.onChange({ target: { value: house.interestRate?.toString() || '' } });
    setCloseDate(house.closeDate || '');
    
    // Initialize Current Value form fields
    currentValueFormat.onChange({ target: { value: house.currentValue?.toString() || '' } });
    mortgageBalanceFormat.onChange({ target: { value: house.mortgageBalance?.toString() || '0' } });
    
    // Initialize Sale Details form fields
    salePriceFormat.onChange({ target: { value: house.finalSalePrice?.toString() || '' } });
    proceedsFormat.onChange({ target: { value: house.totalAmountReceived?.toString() || '' } });
    setSaleDate(house.saleDate || '');
  };

  const handleSaveCombinedInvestmentValue = async () => {
    try {
      setSavingInvestmentValue(true);
      
      // Process Initial Investment data
      const purchasePrice = Number(purchasePriceFormat.value) || 0;
      const downPaymentPercent = Number(downPaymentPercentFormat.value) || 0;
      const downPayment = (purchasePrice * downPaymentPercent) / 100;
      const closingCosts = Number(closingCostsFormat.value) || 0;
      const totalCashInvested = downPayment + closingCosts;
      
      // Process Current Value data
      const currentValue = Number(currentValueFormat.value) || 0;
      const mortgageBalance = Number(mortgageBalanceFormat.value) || 0;
      const equity = currentValue - mortgageBalance;
      
      // Process Sale Details data
      const finalSalePrice = Number(salePriceFormat.value) || 0;
      const totalAmountReceived = Number(proceedsFormat.value) || 0;

      const updatedData = {
        // Initial Investment fields
        purchasePrice,
        downPaymentPercent,
        downPayment,
        closingCosts,
        interestRate: Number(interestRateFormat.value) || 0,
        totalCashInvested,
        closeDate,
        
        // Current Value fields
        currentValue,
        mortgageBalance,
        equity,
        
        // Sale Details fields
        finalSalePrice,
        totalAmountReceived,
        saleDate,
        
        // Add timestamp for metrics update
        lastMetricsUpdate: new Date().toISOString()
      };

      await updateDoc(doc(db, 'properties', id), updatedData);
      
      // Add to metrics history
      const today = new Date().toISOString().split('T')[0]; // Get just the date part (YYYY-MM-DD)
      const metricsHistoryRef = doc(db, 'properties', id, 'roiHistory', today);
      
      // Get current metrics to include in history
      const { cashOnCashAllTime, totalROIAllTime, allTimeNetIncome } = calculateReturns();
      
      await setDoc(metricsHistoryRef, {
        date: today,
        currentValue,
        mortgageBalance,
        equity,
        roi: totalROIAllTime,
        cashOnCash: cashOnCashAllTime,
        netIncome: allTimeNetIncome
      });
      
      setHouse(prev => ({ ...prev, ...updatedData }));
      setEditingInvestmentValue(false);
    } catch (error) {
      console.error('Error saving investment and value data:', error);
    } finally {
      setSavingInvestmentValue(false);
    }
  };

  const handleSaveInitialInvestment = async () => {
    try {
      setSavingInitialInvestment(true);
      const purchasePrice = Number(purchasePriceFormat.value) || 0;
      const downPaymentPercent = Number(downPaymentPercentFormat.value) || 0;
      const downPayment = (purchasePrice * downPaymentPercent) / 100;
      const closingCosts = Number(closingCostsFormat.value) || 0;
      const totalCashInvested = downPayment + closingCosts;

      const updatedData = {
        purchasePrice,
        downPaymentPercent,
        downPayment,
        closingCosts,
        interestRate: Number(interestRateFormat.value) || 0,
        totalCashInvested,
        closeDate,
        lastMetricsUpdate: new Date().toISOString()
      };

      await updateDoc(doc(db, 'properties', id), updatedData);
      setHouse(prev => ({ ...prev, ...updatedData }));
      setEditingInitialInvestment(false);
    } catch (error) {
      console.error('Error saving initial investment:', error);
    } finally {
      setSavingInitialInvestment(false);
    }
  };

  const handleSaveCurrentValue = async () => {
    try {
      setSavingCurrentValue(true);
      const currentValue = Number(currentValueFormat.value) || 0;
      const mortgageBalance = Number(mortgageBalanceFormat.value) || 0;
      const equity = currentValue - mortgageBalance;

      const updatedData = {
        currentValue,
        mortgageBalance,
        equity,
        lastMetricsUpdate: new Date().toISOString()
      };

      await updateDoc(doc(db, 'properties', id), updatedData);
      
      // Add to metrics history
      const today = new Date().toISOString().split('T')[0]; // Get just the date part (YYYY-MM-DD)
      const metricsHistoryRef = doc(db, 'properties', id, 'roiHistory', today);
      
      // Get current metrics to include in history
      const { cashOnCashAllTime, totalROIAllTime, allTimeNetIncome } = calculateReturns();
      
      await setDoc(metricsHistoryRef, {
        date: today,
        currentValue,
        mortgageBalance,
        equity,
        roi: totalROIAllTime,
        cashOnCash: cashOnCashAllTime,
        netIncome: allTimeNetIncome
      });
      
      setHouse(prev => ({ ...prev, ...updatedData }));
      setEditingCurrentValue(false);
    } catch (error) {
      console.error('Error saving current value:', error);
    } finally {
      setSavingCurrentValue(false);
    }
  };

  const calculateReturns = useCallback(() => {
    // Get transactions from last 12 months
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const recentTransactions = transactions.filter(t => new Date(t.date) >= oneYearAgo);

    // Calculate income and expenses
    const calculateTotals = (txns) => {
      return txns.reduce((acc, t) => {
        if (t.type === 'income') {
          acc.income += Number(t.amount);
        } else {
          acc.expenses += Number(t.amount);
        }
        return acc;
      }, { income: 0, expenses: 0 });
    };

    const recentTotals = calculateTotals(recentTransactions);
    const allTimeTotals = calculateTotals(transactions);

    // Calculate net income
    const recentNetIncome = recentTotals.income - recentTotals.expenses;
    const allTimeNetIncome = allTimeTotals.income - allTimeTotals.expenses;

    // Calculate returns
    const totalInvestment = Number(house?.totalCashInvested) || 0;
    const currentValue = Number(house?.currentValue) || 0;
    const mortgageBalance = Number(house?.mortgageBalance) || 0;
    const equity = currentValue - mortgageBalance;

    // Calculate time period since purchase for annualized metrics
    let yearsSincePurchase = 1; // Default to 1 year if no purchase date
    
    // Try to use closeDate first (this is what the user wants)
    if (house?.closeDate) {
      try {
        const closeDate = new Date(house.closeDate);
        const currentDate = new Date();
        
        // Verify we have a valid date
        if (!isNaN(closeDate.getTime())) {
          // Calculate years (or fraction of year) since purchase
          const millisecondsPerDay = 24 * 60 * 60 * 1000;
          const daysSincePurchase = Math.max(1, Math.floor((currentDate - closeDate) / millisecondsPerDay));
          yearsSincePurchase = daysSincePurchase / 365;
          
          console.log('Using close date:', house.closeDate);
          console.log('Years since close:', yearsSincePurchase);
        }
      } catch (error) {
        console.error('Error parsing close date:', error);
      }
    }
    
    // Standard calculations (not annualized)
    const cashOnCashRecent = totalInvestment ? (recentNetIncome / totalInvestment) * 100 : 0;
    const cashOnCashAllTime = totalInvestment ? (allTimeNetIncome / totalInvestment) * 100 : 0;
    const totalROIRecent = totalInvestment ? ((recentNetIncome + (equity - totalInvestment)) / totalInvestment) * 100 : 0;
    const totalROIAllTime = totalInvestment ? ((allTimeNetIncome + (equity - totalInvestment)) / totalInvestment) * 100 : 0;

    // Annualized calculations
    let annualizedCashOnCash = 0;
    if (totalInvestment > 0 && yearsSincePurchase > 0) {
      // Calculate the average annual cash flow
      const annualCashFlow = allTimeNetIncome / yearsSincePurchase;
      // Calculate the annualized cash on cash return
      annualizedCashOnCash = (annualCashFlow / totalInvestment) * 100;
      
      console.log('All time net income:', allTimeNetIncome);
      console.log('Annual cash flow:', annualCashFlow);
      console.log('Total investment:', totalInvestment);
      console.log('Annualized Cash on Cash:', annualizedCashOnCash);
    }

    // Calculate annualized ROI
    let annualizedROI = 0;
    if (totalInvestment > 0 && yearsSincePurchase > 0) {
      // Calculate the total return (cash flow + equity gain)
      const totalReturn = allTimeNetIncome + (equity - totalInvestment);
      
      // Calculate the annualized ROI
      annualizedROI = (totalReturn / totalInvestment / yearsSincePurchase) * 100;
      
      console.log('All time net income:', allTimeNetIncome);
      console.log('Equity gain:', equity - totalInvestment);
      console.log('Total return:', totalReturn);
      console.log('Annualized ROI:', annualizedROI);
    }

    // Update the property with the latest financial metrics and timestamp
    if (house && id) {
      // Only update if the values have changed
      if (house.totalROI !== totalROIAllTime || 
          house.cashOnCash !== cashOnCashAllTime || 
          house.netIncome !== allTimeNetIncome) {
        
        const propertyRef = doc(db, 'properties', id);
        updateDoc(propertyRef, {
          totalROI: totalROIAllTime,
          cashOnCash: cashOnCashAllTime,
          netIncome: allTimeNetIncome,
          lastMetricsUpdate: new Date().toISOString(),
        })
        .then(() => {
          // Add to metrics history
          const today = new Date().toISOString().split('T')[0]; // Get just the date part (YYYY-MM-DD)
          const metricsHistoryRef = doc(db, 'properties', id, 'roiHistory', today);
          
          return setDoc(metricsHistoryRef, {
            date: today,
            currentValue: Number(house?.currentValue) || 0,
            mortgageBalance: Number(house?.mortgageBalance) || 0,
            equity: (Number(house?.currentValue) || 0) - (Number(house?.mortgageBalance) || 0),
            roi: totalROIAllTime,
            cashOnCash: cashOnCashAllTime,
            netIncome: allTimeNetIncome
          });
        })
        .catch(error => {
          console.error('Error updating property metrics:', error);
        });
      }
    }

    return {
      cashOnCashRecent,
      cashOnCashAllTime,
      totalROIRecent,
      totalROIAllTime,
      recentIncome: recentTotals.income,
      recentExpenses: recentTotals.expenses,
      recentNetIncome,
      allTimeIncome: allTimeTotals.income,
      allTimeExpenses: allTimeTotals.expenses,
      allTimeNetIncome,
      yearsSincePurchase,
      annualizedROI,
      annualizedCashOnCash
    };
  }, [transactions, house, id]);

  useEffect(() => {
    if (!id) return;

    const q = query(
      collection(db, 'transactions'),
      where('propertyId', '==', id),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const transactionData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTransactions(transactionData);
    });

    return () => unsubscribe();
  }, [id]);

  const calculateAnnualizedReturn = () => {
    if (!house?.closeDate || !house?.saleDate) return 0;

    const startDate = new Date(house.closeDate);
    const endDate = new Date(house.saleDate);
    const yearsHeld = (endDate - startDate) / (365 * 24 * 60 * 60 * 1000);
    
    if (yearsHeld <= 0) return 0;

    const totalReturn = calculateTotalReturn();
    const totalReturnPercentage = (totalReturn / (Number(house?.totalCashInvested) || 1)) * 100;
    const annualizedReturn = (Math.pow(1 + (totalReturnPercentage / 100), 1 / yearsHeld) - 1) * 100;
    
    return annualizedReturn;
  };

  const calculateTotalReturn = () => {
    if (!house) return 0;
    
    const netIncome = calculateReturns().allTimeNetIncome;
    const totalAmountReceived = Number(house.totalAmountReceived || 0);
    const totalCashInvested = Number(house.totalCashInvested || 0);
    
    return netIncome + totalAmountReceived - totalCashInvested;
  };

  const formatTotalReturnPercentage = () => {
    const totalReturn = calculateTotalReturn();
    const cashInvested = Number(house?.totalCashInvested) || 1;
    const percentage = (totalReturn / cashInvested) * 100;
    return percentage.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleSellProperty = async () => {
    setIsSaving(true);
    try {
      const docRef = doc(db, 'properties', id);
      await updateDoc(docRef, {
        status: saleData.status,
        salePrice: Number(saleData.salePrice),
        saleProceedsPercentage: Number(saleData.proceedsPercentage),
        updatedAt: new Date().toISOString()
      });
      setHouse(prev => ({
        ...prev,
        status: saleData.status,
        salePrice: Number(saleData.salePrice),
        saleProceedsPercentage: Number(saleData.proceedsPercentage)
      }));
      setShowSellModal(false);
    } catch (error) {
      console.error('Error selling property:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSaleDetails = async () => {
    try {
      const docRef = doc(db, 'properties', id);
      
      // Ensure we have valid numbers or null
      const finalSalePrice = salePriceFormat.value ? parseFloat(salePriceFormat.value) : null;
      const totalAmountReceived = proceedsFormat.value ? parseFloat(proceedsFormat.value) : null;
      
      // Create update object
      const updateData = {
        finalSalePrice,
        totalAmountReceived,
        saleDate: saleDate || null,
        updatedAt: new Date().toISOString()
      };

      // Log the update for debugging
      console.log('Updating property with:', updateData);
      
      // Perform the update
      await updateDoc(docRef, updateData);

      // Update local state
      setHouse(prev => ({
        ...prev,
        finalSalePrice,
        totalAmountReceived,
        saleDate: saleDate || null
      }));
      
      // Add to metrics history if sale details were updated
      if (finalSalePrice || totalAmountReceived) {
        const today = new Date().toISOString().split('T')[0]; // Get just the date part (YYYY-MM-DD)
        const metricsHistoryRef = doc(db, 'properties', id, 'roiHistory', today);
        
        // Get current metrics to include in history
        const { cashOnCashAllTime, totalROIAllTime, allTimeNetIncome } = calculateReturns();
        
        await setDoc(metricsHistoryRef, {
          date: today,
          currentValue: Number(house?.currentValue) || 0,
          mortgageBalance: Number(house?.mortgageBalance) || 0,
          equity: (Number(house?.currentValue) || 0) - (Number(house?.mortgageBalance) || 0),
          roi: totalROIAllTime,
          cashOnCash: cashOnCashAllTime,
          netIncome: allTimeNetIncome,
          finalSalePrice,
          totalAmountReceived
        });
      }

      setEditingSaleDetails(false);
    } catch (error) {
      console.error('Error updating sale details:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`Error updating sale details: ${error.message}`);
    }
  };

  const [statusAnchorEl, setStatusAnchorEl] = useState(null);

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
    setShowStatusPopover(true);
  };

  const handleStatusClose = () => {
    setShowStatusPopover(false);
    setStatusAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      const houseRef = doc(db, 'properties', id);
      await updateDoc(houseRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      // Update local state
      setHouse(prevHouse => ({
        ...prevHouse,
        status: newStatus,
        updatedAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const StatusPopover = ({ isOpen, onClose, currentStatus, onStatusChange, anchorEl }) => {
    const popoverRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (popoverRef.current && 
            !popoverRef.current.contains(event.target) && 
            !anchorEl?.contains(event.target)) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [isOpen, onClose, anchorEl]);

    const handleMouseEnter = (e) => {
      e.currentTarget.style.opacity = '0.8';
    };

    const handleMouseLeave = (e) => {
      e.currentTarget.style.opacity = '1';
    };

    if (!isOpen) return null;

    return (
      <div 
        ref={popoverRef}
        className="position-fixed shadow rounded" 
        style={{
          zIndex: 1000,
          top: anchorEl?.getBoundingClientRect().bottom + 5,
          left: anchorEl?.getBoundingClientRect().left,
          minWidth: Math.max(anchorEl?.getBoundingClientRect().width || 120, 200),
          backgroundColor: '#212529',
          border: '1px solid #373b3e',
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          overflow: 'hidden'
        }}
      >
        <div className="p-2 border-bottom border-secondary">
          <h6 className="m-0 text-white fw-bold">Change Status</h6>
        </div>
        <div className="p-2">
          {Object.entries(HOUSE_STATUSES).map(([key, value]) => (
            <div
              key={key}
              className={`d-flex align-items-center p-2 rounded mb-1 ${HOUSE_STATUS_CLASSES[key]}`}
              onClick={() => {
                onStatusChange(key);
                onClose();
              }}
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: currentStatus === key ? 0.85 : 1,
                position: 'relative',
                paddingLeft: '10px',
                borderRadius: '0.25rem'
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {currentStatus === key && (
                <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '0.75rem' }}></i>
              )}
              <span className={currentStatus === key ? 'fw-bold' : ''}>
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleAddComp = async (compData) => {
    try {
      // Add userId to the comp data to ensure proper permissions
      const compWithUserId = {
        ...compData,
        userId: auth.currentUser.uid,
        createdBy: auth.currentUser.uid,
        createdAt: new Date().toISOString()
      };
      
      if (editingComp) {
        await updateDoc(doc(db, 'properties', id, 'comps', editingComp.id), compWithUserId);
      } else {
        await addDoc(collection(db, 'properties', id, 'comps'), compWithUserId);
      }
      setEditingComp(null);
      return true;
    } catch (error) {
      console.error('Error adding comp:', error);
      throw error;
    }
  };

  const handleEditComp = (comp) => {
    setEditingComp({
      ...comp,
      price: Number(comp.price)  // Ensure price is passed as a number
    });
    setIsCompModalOpen(true);
  };

  if (isLoading) return <LoadingSpinner />;
  if (!house) return <div>House not found</div>;

  return (
    <div className="container-fluid px-4 py-4">
      <Breadcrumb propertyId={id} />
      
      <div className="row mb-4 align-items-start">
        <div className="col">
          <h1 className="mb-2">{house.address.street}</h1>
          <p className="text-muted mb-2">
            {`${house.address.city}, ${house.address.state} ${house.address.zipCode}`}
          </p>
          <div className="d-flex align-items-center mb-2">
            <span className="text-white">{house.beds ? `${Number(house.beds)} beds` : '- beds'}</span>
            <span className="text-muted mx-2">•</span>
            <span className="text-white">{house.baths ? `${Number(house.baths)} baths` : '- baths'}</span>
            <span className="text-muted mx-2">•</span>
            <span className="text-white">{house.squareFootage ? `${Number(house.squareFootage).toLocaleString()} sq ft` : '- sq ft'}</span>
            {house.link && (
              <>
                <span className="text-muted mx-2">•</span>
                <a 
                  href={house.link.startsWith('http') ? house.link : `https://${house.link}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary text-decoration-none"
                  title="View property listing"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>Link
                </a>
              </>
            )}
          </div>
          <div className="position-relative d-inline-block">
            <div 
              className={`badge ${HOUSE_STATUS_CLASSES[house.status]} py-2 px-3`}
              onClick={handleStatusClick}
              style={{ 
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
                opacity: isUpdatingStatus ? 0.7 : 1,
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '0.375rem',
                ':hover': {
                  opacity: 0.8
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = isUpdatingStatus ? '0.7' : '1';
              }}
              ref={statusButtonRef}
            >
              {HOUSE_STATUSES[house.status]}
            </div>
            <StatusPopover
              isOpen={showStatusPopover}
              onClose={handleStatusClose}
              currentStatus={house.status}
              onStatusChange={handleStatusChange}
              anchorEl={statusAnchorEl}
            />
          </div>
        </div>
        <div className="col-auto d-flex align-items-start gap-2">
          <button 
            className="btn btn-primary btn-sm" 
            onClick={() => setIsEditModalOpen(true)}
          >
            <i className="bi bi-pencil-square me-2"></i>Edit House
          </button>
        </div>
      </div>

      <div className="row g-2">
        {/* List Price and Target Metrics Section */}
        <div className="col-12 mb-2">
          <div className="row g-3">
            {/* List Price Card */}
            <div className="col-md-6">
              <div className="card bg-dark border-0">
                <div className="card-body p-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="card-title m-0 h6">List Price</h5>
                    {!editingDetails ? (
                      <button 
                        className="btn btn-link text-light p-0" 
                        onClick={() => setEditingDetails(true)}
                        title="Edit list price"
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                    ) : (
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm btn-outline-secondary" 
                          onClick={() => setEditingDetails(false)}
                          disabled={savingDetails}
                        >
                          Cancel
                        </button>
                        <button 
                          className="btn btn-sm btn-primary d-flex align-items-center justify-content-center gap-2" 
                          onClick={() => {
                            handleDetailChange('listPrice', listPriceFormat.value);
                            handleSaveCombinedDetails();
                          }}
                          disabled={savingDetails}
                        >
                          {savingDetails ? (
                            <>
                              <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <span>Saving</span>
                            </>
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="rounded p-2" style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(33, 37, 41, 0.7)' }}>
                    {!editingDetails ? (
                      <div className="text-center">
                        <div className="text-muted small mb-1">List Price</div>
                        <div 
                          className="text-white fw-bold fs-5"
                          title="Click to edit List Price"
                          onClick={() => setEditingDetails(true)}
                          style={{ cursor: 'pointer' }}
                        >
                          ${Number(house.listPrice || 0).toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-muted small mb-1">List Price</div>
                        <div className="input-group">
                          <span className="input-group-text bg-dark text-white border-secondary">$</span>
                          <input
                            type="text"
                            className="form-control bg-dark text-white border-secondary"
                            value={listPriceFormat.formatted}
                            onChange={listPriceFormat.onChange}
                            placeholder="List Price"
                          />
                        </div>

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Target Metrics Card */}
            <div className="col-md-6">
              <div className="card bg-dark border-0">
                <div className="card-body p-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="card-title m-0 h6">Target Metrics</h5>
                    {!editingTargets ? (
                      <button 
                        className="btn btn-link text-light p-0" 
                        onClick={() => setEditingTargets(true)}
                        title="Edit target metrics"
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                    ) : (
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm btn-outline-secondary" 
                          onClick={() => setEditingTargets(false)}
                          disabled={savingTargets}
                        >
                          Cancel
                        </button>
                        <button 
                          className="btn btn-sm btn-primary d-flex align-items-center justify-content-center gap-2" 
                          onClick={saveReturnTargets}
                          disabled={savingTargets}
                        >
                          {savingTargets ? (
                            <>
                              <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <span>Saving</span>
                            </>
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="rounded p-2" style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', backgroundColor: 'rgba(30, 58, 138, 0.7)' }}>
                    {!editingTargets ? (
                      <div>
                        <div className="d-flex">
                          <div className="flex-grow-1 text-center">
                            <div className="text-muted small mb-1">Cap Rate</div>
                            <div 
                              className="text-white fw-bold fs-5"
                              onClick={() => setEditingTargets(true)}
                              style={{ cursor: 'pointer' }}
                              title="Click to edit Cap Rate target"
                            >
                              {house.targetCapRate ? `${Number(house.targetCapRate).toFixed(1)}%` : '-'}
                            </div>
                          </div>
                          <div className="border-end border-secondary mx-2"></div>
                          <div className="flex-grow-1 text-center">
                            <div className="text-muted small mb-1">Cash on Cash</div>
                            <div 
                              className="text-white fw-bold fs-5"
                              onClick={() => setEditingTargets(true)}
                              style={{ cursor: 'pointer' }}
                              title="Click to edit Cash on Cash target"
                            >
                              {house.targetCoCReturn ? `${Number(house.targetCoCReturn).toFixed(1)}%` : '-'}
                            </div>
                          </div>
                          <div className="border-end border-secondary mx-2"></div>
                          <div className="flex-grow-1 text-center">
                            <div className="text-muted small mb-1">Total ROI</div>
                            <div 
                              className="text-white fw-bold fs-5"
                              onClick={() => setEditingTargets(true)}
                              style={{ cursor: 'pointer' }}
                              title="Click to edit Total ROI target"
                            >
                              {house.targetTotalROI ? `${Number(house.targetTotalROI).toFixed(1)}%` : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="row g-2">
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">Cap Rate</label>
                            <div className="input-group input-group-sm">
                              <input
                                type="number"
                                className="form-control bg-dark text-white border-secondary"
                                value={targetCapRate}
                                onChange={(e) => setTargetCapRate(e.target.value)}
                                min="0"
                                step="0.1"
                              />
                              <span className="input-group-text bg-dark text-white border-secondary">%</span>
                            </div>
                          </div>
                          
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">Cash on Cash</label>
                            <div className="input-group input-group-sm">
                              <input
                                type="number"
                                className="form-control bg-dark text-white border-secondary"
                                value={targetCoCReturn}
                                onChange={(e) => setTargetCoCReturn(e.target.value)}
                                min="0"
                                step="0.1"
                              />
                              <span className="input-group-text bg-dark text-white border-secondary">%</span>
                            </div>
                          </div>
                          
                          <div className="col-4">
                            <label className="form-label small text-muted mb-1">Total ROI</label>
                            <div className="input-group input-group-sm">
                              <input
                                type="number"
                                className="form-control bg-dark text-white border-secondary"
                                value={targetTotalROI}
                                onChange={(e) => setTargetTotalROI(e.target.value)}
                                min="0"
                                step="0.1"
                              />
                              <span className="input-group-text bg-dark text-white border-secondary">%</span>
                            </div>
                          </div>
                        </div>
                      

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scenarios Card */}
        <div className="col-12">
          <div className="card bg-dark border-secondary">
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <h5 className="card-title m-0 h6">Scenarios</h5>
                </div>
                
                <div className="d-flex gap-2">
                  {!isComparing ? (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setIsComparing(true)}
                      disabled={scenarios.length < 2}
                    >
                      <i className="bi bi-graph-up me-2"></i>Compare Scenarios
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setIsComparing(false);
                        setSelectedScenarios([]);
                      }}
                    >
                      <i className="bi bi-x-circle me-2"></i>Cancel Comparison
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setIsScenarioModalOpen(true)}
                  >
                    <i className="bi bi-plus-circle me-2"></i>Add Scenario
                  </button>
                </div>
              </div>

              {scenarios.length === 0 ? (
                <div className="text-center my-5">
                  <i className="bi bi-clipboard-data text-muted mb-4" style={{ fontSize: '4rem' }}></i>
                  <p className="text-muted mb-2">No scenarios added yet</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-dark table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        {isComparing && (
                          <th className="border-0 text-center" style={{ width: '50px' }}>
                            Select
                          </th>
                        )}
                        <th 
                          className="border-0" 
                          onClick={() => {
                            if (sortField === 'name') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('name');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <span>Name</span>
                            <i className={`bi bi-arrow-${sortField === 'name' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'name' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'name' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0"
                          onClick={() => {
                            if (sortField === 'price') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('price');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <span>Offer</span>
                            <i className={`bi bi-arrow-${sortField === 'price' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'price' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'price' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0"
                          onClick={() => {
                            if (sortField === 'downPaymentPercent') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('downPaymentPercent');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <span>Down Payment</span>
                            <i className={`bi bi-arrow-${sortField === 'downPaymentPercent' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'downPaymentPercent' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'downPaymentPercent' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0"
                          onClick={() => {
                            if (sortField === 'interestRate') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('interestRate');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <span>Interest Rate</span>
                            <i className={`bi bi-arrow-${sortField === 'interestRate' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'interestRate' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'interestRate' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0"
                          onClick={() => {
                            if (sortField === 'closingCosts') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('closingCosts');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <span>Closing Costs</span>
                            <i className={`bi bi-arrow-${sortField === 'closingCosts' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'closingCosts' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'closingCosts' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0"
                          onClick={() => {
                            if (sortField === 'cashToClose') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('cashToClose');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <span>Cash to Close</span>
                            <i className={`bi bi-arrow-${sortField === 'cashToClose' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'cashToClose' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'cashToClose' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0 text-center"
                          onClick={() => {
                            if (sortField === 'monthlyCashFlow') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('monthlyCashFlow');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <span>Monthly Cash Flow</span>
                            <i className={`bi bi-arrow-${sortField === 'monthlyCashFlow' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'monthlyCashFlow' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'monthlyCashFlow' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0 text-center"
                          onClick={() => {
                            if (sortField === 'cashOnCashReturn') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('cashOnCashReturn');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <span>Cash on Cash</span>
                            <i className={`bi bi-arrow-${sortField === 'cashOnCashReturn' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'cashOnCashReturn' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'cashOnCashReturn' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0 text-center"
                          onClick={() => {
                            if (sortField === 'totalROI') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('totalROI');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <span>Total ROI</span>
                            <i className={`bi bi-arrow-${sortField === 'totalROI' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'totalROI' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'totalROI' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th 
                          className="border-0 text-center"
                          onClick={() => {
                            if (sortField === 'capRate') {
                              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('capRate');
                              setSortDirection('asc');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <span>Cap Rate</span>
                            <i className={`bi bi-arrow-${sortField === 'capRate' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down-up'} ms-1 ${sortField === 'capRate' ? 'text-primary' : 'text-muted opacity-50'}`} style={{ fontSize: sortField === 'capRate' ? '0.85rem' : '0.7rem' }}></i>
                          </div>
                        </th>
                        <th className="border-0"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {scenarios
                        .slice()
                        .sort((a, b) => {
                          // If no sort field is set, don't sort
                          if (!sortField) return 0;
                          
                          // Handle special cases for nested properties
                          if (sortField === 'monthlyCashFlow') {
                            const aValue = Number(a.calculations?.annualCashFlow / 12 || 0);
                            const bValue = Number(b.calculations?.annualCashFlow / 12 || 0);
                            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                          } else if (sortField === 'capRate') {
                            const aValue = Number(a.calculations?.capRate || 0);
                            const bValue = Number(b.calculations?.capRate || 0);
                            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                          } else if (sortField === 'cashOnCashReturn') {
                            const aValue = Number(a.calculations?.annualCashOnCashReturn || 0);
                            const bValue = Number(b.calculations?.annualCashOnCashReturn || 0);
                            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                          } else if (sortField === 'totalROI') {
                            const aValue = Number(a.calculations?.annualROI || 0);
                            const bValue = Number(b.calculations?.annualROI || 0);
                            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                          }
                          
                          // Handle regular number fields
                          if (['price', 'downPaymentPercent', 'interestRate', 'closingCosts', 'cashToClose'].includes(sortField)) {
                            const aValue = Number(a[sortField] || 0);
                            const bValue = Number(b[sortField] || 0);
                            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                          }
                          
                          // Default string comparison for name and other string fields
                          const aValue = a[sortField] || '';
                          const bValue = b[sortField] || '';
                          return sortDirection === 'asc' 
                            ? aValue.localeCompare(bValue) 
                            : bValue.localeCompare(aValue);
                        })
                        .slice((scenariosCurrentPage - 1) * scenariosPerPage, scenariosCurrentPage * scenariosPerPage)
                        .map((scenario, index) => (
                        <tr
                          key={scenario.id}
                          onClick={(e) => {
                            if (!isComparing) {
                              navigate(`/scenario/${scenario.id}`);
                            }
                          }}
                          role="button"
                          className={`${isComparing ? '' : 'cursor-pointer'} ${index === 0 ? 'border-top border-top-thick' : ''}`}
                        >
                          {isComparing && (
                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="form-check d-flex justify-content-center">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedScenarios.includes(scenario.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (selectedScenarios.length < 3) {
                                        setSelectedScenarios([...selectedScenarios, scenario.id]);
                                      }
                                    } else {
                                      setSelectedScenarios(selectedScenarios.filter(id => id !== scenario.id));
                                    }
                                  }}
                                  disabled={!selectedScenarios.includes(scenario.id) && selectedScenarios.length >= 3}
                                />
                              </div>
                            </td>
                          )}
                          <td>{scenario.name}</td>
                          <td>${Number(scenario.price || 0).toLocaleString()}</td>
                          <td>{scenario.downPaymentPercent}%</td>
                          <td>{Number(scenario.interestRate || 0).toFixed(2)}%</td>
                          <td>${Number(scenario.closingCosts || 0).toLocaleString()}</td>
                          <td>${Number(scenario.cashToClose || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="text-center">
                            <div className={`badge rounded-pill ${Number(scenario.calculations?.annualCashFlow / 12 || 0) > 0 ? 'bg-success bg-opacity-75' : 'bg-danger bg-opacity-75'}`}>
                              ${Number(scenario.calculations?.annualCashFlow / 12 || 0).toFixed(2)}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className={`badge rounded-pill ${Number(scenario.calculations?.annualCashOnCashReturn || 0) >= Number(house.targetCoCReturn || 0) ? 'bg-success bg-opacity-75' : 'bg-danger bg-opacity-75'}`}>
                              {Number(scenario.calculations?.annualCashOnCashReturn || 0).toFixed(2)}%
                            </div>
                          </td>
                          <td className="text-center">
                            <div className={`badge rounded-pill ${Number(scenario.calculations?.annualROI || 0) >= Number(house.targetTotalROI || 0) ? 'bg-success bg-opacity-75' : 'bg-danger bg-opacity-75'}`}>
                              {Number(scenario.calculations?.annualROI || 0).toFixed(2)}%
                            </div>
                          </td>
                          <td className="text-center">
                            <div className={`badge rounded-pill ${Number(scenario.calculations?.capRate || 0) >= Number(house.targetCapRate || 0) ? 'bg-success bg-opacity-75' : 'bg-danger bg-opacity-75'}`}>
                              {Number(scenario.calculations?.capRate || 0).toFixed(2)}%
                            </div>
                          </td>
                          <td className="text-end">
                            <div className="dropdown">
                              <button
                                className="btn btn-link text-white p-0"
                                type="button" 
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDropdownToggle(scenario.id, openDropdownId !== scenario.id);
                                }}
                              >
                                <i className="bi bi-caret-down-fill fs-5"></i>
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end bg-dark">
                                <li>
                                  <button 
                                    className="dropdown-item text-danger d-flex align-items-center" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setScenarioToDelete(scenario);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    <i className="bi bi-trash me-2"></i>Delete
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {scenarios.length > scenariosPerPage && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <button 
                      className="btn btn-sm btn-outline-secondary me-2" 
                      disabled={scenariosCurrentPage === 1}
                      onClick={() => setScenarioCurrentPage(prev => Math.max(prev - 1, 1))}
                    >
                      Previous
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary" 
                      disabled={scenariosCurrentPage * scenariosPerPage >= scenarios.length}
                      onClick={() => setScenarioCurrentPage(prev => prev + 1)}
                    >
                      Next
                    </button>
                  </div>
                  <div className="text-muted small">
                    Showing {Math.min((scenariosCurrentPage - 1) * scenariosPerPage + 1, scenarios.length)} to {Math.min(scenariosCurrentPage * scenariosPerPage, scenarios.length)} of {scenarios.length} entries
                  </div>
                </div>
              )}
              
              {/* Scenario Comparison Charts */}
              {isComparing && selectedScenarios.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-3">Scenario Comparison (10 Year Projection)</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="card bg-dark border-secondary">
                        <div className="card-body">
                          <h6 className="card-title mb-3">Cash on Cash Return</h6>
                          <div style={{ height: '300px' }}>
                            <Line
                              data={{
                                labels: Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`),
                                datasets: selectedScenarios.map((scenarioId, index) => {
                                  const scenario = scenarios.find(s => s.id === scenarioId);
                                  return {
                                    label: scenario?.name || 'Unknown',
                                    data: Array.from({ length: 10 }, (_, i) => {
                                      // First check if we have stored projections in the house record
                                      if (house.scenarioProjections && 
                                          house.scenarioProjections[scenario.id] && 
                                          house.scenarioProjections[scenario.id].chartProjections && 
                                          house.scenarioProjections[scenario.id].chartProjections.cashOnCash) {
                                        
                                        // Use the stored projections from the house record
                                        return Number((house.scenarioProjections[scenario.id].chartProjections.cashOnCash[i] || 0).toFixed(2));
                                      }
                                      
                                      // Use scenario's own chart projections if available
                                      if (scenario.chartProjections && scenario.chartProjections.cashOnCash) {
                                        return Number((scenario.chartProjections.cashOnCash[i] || 0).toFixed(2));
                                      }
                                      
                                      // If no projections are available, use base value for all years
                                      const baseReturn = Number(scenario?.calculations?.annualCashOnCashReturn || 0);
                                      return Number(baseReturn.toFixed(2));
                                    }),
                                    borderColor: [
                                      '#4bc0c0',
                                      '#ff6384',
                                      '#36a2eb',
                                    ][index % 3],
                                    backgroundColor: [
                                      'rgba(75, 192, 192, 0.2)',
                                      'rgba(255, 99, 132, 0.2)',
                                      'rgba(54, 162, 235, 0.2)',
                                    ][index % 3],
                                    tension: 0.1
                                  };
                                })
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    title: {
                                      display: true,
                                      text: 'Percentage (%)',
                                      color: 'white'
                                    },
                                    grid: {
                                      color: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    ticks: {
                                      color: 'white'
                                    }
                                  },
                                  x: {
                                    grid: {
                                      color: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    ticks: {
                                      color: 'white'
                                    }
                                  }
                                },
                                plugins: {
                                  legend: {
                                    labels: {
                                      color: 'white'
                                    }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: function(context) {
                                        return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
                                      }
                                    }
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card bg-dark border-secondary">
                        <div className="card-body">
                          <h6 className="card-title mb-3">Total ROI</h6>
                          <div style={{ height: '300px' }}>
                            <Line
                              data={{
                                labels: Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`),
                                datasets: selectedScenarios.map((scenarioId, index) => {
                                  const scenario = scenarios.find(s => s.id === scenarioId);
                                  return {
                                    label: scenario?.name || 'Unknown',
                                    data: Array.from({ length: 10 }, (_, i) => {
                                      // First check if we have stored projections in the house record
                                      if (house.scenarioProjections && 
                                          house.scenarioProjections[scenario.id] && 
                                          house.scenarioProjections[scenario.id].chartProjections && 
                                          house.scenarioProjections[scenario.id].chartProjections.totalROI) {
                                        
                                        // Use the stored projections from the house record
                                        return Number((house.scenarioProjections[scenario.id].chartProjections.totalROI[i] || 0).toFixed(2));
                                      }
                                      
                                      // Use scenario's own chart projections
                                      if (scenario.chartProjections && scenario.chartProjections.totalROI) {
                                        return Number((scenario.chartProjections.totalROI[i] || 0).toFixed(2));
                                      }
                                      
                                      // If no projections are available, use base value for all years
                                      const baseReturn = Number(scenario?.calculations?.annualROI || 0);
                                      return Number(baseReturn.toFixed(2));
                                    }),
                                    borderColor: [
                                      '#4bc0c0',
                                      '#ff6384',
                                      '#36a2eb',
                                    ][index % 3],
                                    backgroundColor: [
                                      'rgba(75, 192, 192, 0.2)',
                                      'rgba(255, 99, 132, 0.2)',
                                      'rgba(54, 162, 235, 0.2)',
                                    ][index % 3],
                                    tension: 0.1
                                  };
                                })
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    title: {
                                      display: true,
                                      text: 'Percentage (%)',
                                      color: 'white'
                                    },
                                    grid: {
                                      color: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    ticks: {
                                      color: 'white'
                                    }
                                  },
                                  x: {
                                    grid: {
                                      color: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    ticks: {
                                      color: 'white'
                                    }
                                  }
                                },
                                plugins: {
                                  legend: {
                                    labels: {
                                      color: 'white'
                                    }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: function(context) {
                                        return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
                                      }
                                    }
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddScenarioModal      />
      
      <EditHouseModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        house={house}
        propertyId={id}
        onUpdate={() => fetchData()}
      />

      <AddCompModal
        isOpen={isCompModalOpen}
        onClose={() => {
          setIsCompModalOpen(false);
          setEditingComp(null);
        }}
        onSave={handleAddComp}
        initialData={editingComp}
      />

      {/* Delete Confirmation Modal */}
      <div className={`modal ${showDeleteModal ? 'show' : ''}`} 
           style={{ display: showDeleteModal ? 'block' : 'none' }}
           onClick={(e) => {
             if (e.target.className.includes('modal ')) {
               setShowDeleteModal(false);
               setScenarioToDelete(null);
             }
           }}
           tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content bg-dark text-white border-secondary">
            <div className="modal-header border-secondary">
              <h5 className="modal-title">Delete Scenario</h5>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setScenarioToDelete(null);
                }}
              ></button>
            </div>
            <div className="modal-body">
              <p className="mb-3">Are you sure you want to delete the following scenario?</p>
              <p className="text-center mb-0 fw-bold">{scenarioToDelete?.name}</p>
            </div>
            <div className="modal-footer border-secondary">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setScenarioToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={handleDeleteScenario}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal-backdrop fade ${showDeleteModal ? 'show' : ''}`}
           style={{ display: showDeleteModal ? 'block' : 'none' }}></div>

      <BaseModal
        isOpen={showSaleSimulator}
        onClose={() => setShowSaleSimulator(false)}
        title="Sale Simulator"
        footer={
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowSaleSimulator(false)}
          >
            Cancel
          </button>
        }
      >
        <div className="p-3">
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label small">Sale Price</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="text"
                  className="form-control"
                  value={salePriceFormat.formatted}
                  onChange={(e) => {
                    const numericValue = Number(e.target.value.replace(/[^\d.]/g, '')) || 0;
                    salePriceFormat.onChange(e);
                    setSaleSimulation({
                      ...saleSimulation,
                      salePrice: numericValue
                    });
                  }}
                />
              </div>
            </div>
            <div className="col-12">
              <label className="form-label small">Mortgage Balance</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="text"
                  className="form-control"
                  value={mortgageBalanceFormat.formatted}
                  onChange={(e) => {
                    const numericValue = Number(e.target.value.replace(/[^\d.]/g, '')) || 0;
                    mortgageBalanceFormat.onChange(e);
                    setSaleSimulation({
                      ...saleSimulation,
                      mortgageBalance: numericValue
                    });
                  }}
                />
              </div>
            </div>
            <div className="col-12">
              <label className="form-label small">Sale Proceeds Percentage</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={saleSimulation.salePercentage === 0 ? '' : saleSimulation.salePercentage}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : Number(e.target.value);
                    setSaleSimulation({
                      ...saleSimulation,
                      salePercentage: value,
                    });
                  }}
                />
                <span className="input-group-text">%</span>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="row g-3">
            <div className="col-12">
              <h6 className="text-muted small mb-3">Sale Analysis</h6>
            </div>
            <div className="col-md-6">
              <div className="small text-muted mb-1">Current Equity</div>
              <div className="h6 mb-0">
                ${(Number(saleSimulation.salePrice || 0) - Number(saleSimulation.mortgageBalance || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="col-md-6">
              <div className="small text-muted mb-1">Net Sale Proceeds</div>
              <div className="h6 mb-0">
                ${((Number(saleSimulation.salePrice || 0) * (saleSimulation.salePercentage / 100)) - Number(saleSimulation.mortgageBalance || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="col-md-6">
              <div className="small text-muted mb-1">All Time Net Income</div>
              <div className="h6 mb-0">
                ${calculateReturns().allTimeNetIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="col-md-6">
              <div className="small text-muted mb-1">Total Cash Made</div>
              <div className="h6 mb-0">
                ${(calculateReturns().allTimeNetIncome + ((Number(saleSimulation.salePrice || 0) * (saleSimulation.salePercentage / 100)) - Number(saleSimulation.mortgageBalance || 0))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="col-12">
              <div className="small text-muted mb-1">Total ROI</div>
              <div className="h5 mb-0">
                {(((calculateReturns().allTimeNetIncome + ((Number(saleSimulation.salePrice || 0) * (saleSimulation.salePercentage / 100)) - Number(saleSimulation.mortgageBalance || 0))) / (Number(house?.totalCashInvested) || 1)) * 100).toFixed(2)}%
              </div>
            </div>
          </div>
        </div>
      </BaseModal>

      <BaseModal
        isOpen={showSellModal}
        onClose={() => setShowSellModal(false)}
        title="Sell Property"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSellModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSellProperty}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        <form className="needs-validation" noValidate>
          <div className="mb-3">
            <label className="form-label">Status</label>
            <select
              className="form-select bg-dark text-light border-secondary"
              value={saleData.status}
              onChange={(e) => setSaleData(prev => ({ ...prev, status: e.target.value }))}
            >
              {Object.entries(HOUSE_STATUSES).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Sale Price</label>
            <div className="input-group">
              <span className="input-group-text bg-dark text-light border-secondary">$</span>
              <input
                type="text"
                className="form-control bg-dark text-light border-secondary"
                value={saleData.salePrice}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setSaleData(prev => ({
                    ...prev,
                    salePrice: value,
                    formattedSalePrice: Number(value).toLocaleString()
                  }));
                }}
                placeholder="Enter sale price"
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Sale Proceeds Percentage</label>
            <div className="input-group">
              <input
                type="number"
                className="form-control bg-dark text-light border-secondary"
                value={saleData.proceedsPercentage}
                onChange={(e) => setSaleData(prev => ({ ...prev, proceedsPercentage: e.target.value }))}
                placeholder="94"
                min="0"
                max="100"
                step="0.1"
              />
              <span className="input-group-text bg-dark text-light border-secondary">%</span>
            </div>
          </div>
        </form>
      </BaseModal>
      <style jsx="true">{`
        .nav-tabs {
          border-bottom: none;
        }
        
        .nav-tabs .nav-link {
          border: none;
          border-radius: 0;
          padding: 0.5rem 1rem;
          transition: all 0.2s ease;
          font-weight: 500;
          color: #6c757d;
        }
        
        .nav-tabs .nav-link:not(.active) {
          background-color: transparent;
          box-shadow: none;
        }
        
        .nav-tabs .nav-link.active {
          border: none;
          border-bottom: 3px solid white;
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          background-color: transparent !important;
          color: white;
          font-weight: 600;
        }
        
        .tab-divider {
          width: 100%;
          height: 1px;
          background-color: #6c757d;
          margin-top: 0;
        }
      `}</style>

      {/* Delete Comp Confirmation Modal */}
      <BaseModal
        isOpen={showDeleteCompModal}
        onClose={() => {
          setShowDeleteCompModal(false);
          setCompToDelete(null);
        }}
        title="Delete Comparable Property"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowDeleteCompModal(false);
                setCompToDelete(null);
              }}
              disabled={isDeletingComp}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={async () => {
                if (!compToDelete) return;
                
                setIsDeletingComp(true);
                try {
                  await deleteDoc(doc(db, 'properties', id, 'comps', compToDelete.id));
                  setShowDeleteCompModal(false);
                  setCompToDelete(null);
                } catch (error) {
                  console.error('Error deleting comp:', error);
                } finally {
                  setIsDeletingComp(false);
                }
              }}
              disabled={isDeletingComp}
            >
              {isDeletingComp ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete this comparable?</p>
      </BaseModal>
      
      {/* Save/cancel buttons moved into the card */}
      
      {/* Add Scenario Modal */}
      <AddScenarioModal
        isOpen={isScenarioModalOpen}
        onClose={() => setIsScenarioModalOpen(false)}
        houseId={id}
        onScenarioAdded={() => {
          // Refresh scenarios after adding a new one
          setIsScenarioModalOpen(false);
        }}
      />
    </div>
  );
};

export default HouseDetail;
